/**
 * The queue page's Items tab: every item in the queue with its status, each
 * score's answer and who completed it — Langfuse's queue items table. A filter
 * narrows it to pending, skipped (in this tab) or completed items; a click opens
 * that item in Review. The row being reviewed is marked.
 */

import { useState } from "react";
import { Box } from "@/components/ui/box";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Pill } from "@/components/ui/pill";
import { EmptyState } from "@/components/ui/states";
import { Segmented } from "@/components/ui/tabs";
import { formatRelative, preview } from "@/lib/format";
import { answerLabel } from "./scores";
import type { QueueItem, ScoreConfig } from "./types";

type Filter = "all" | "pending" | "skipped" | "completed";
type Row = QueueItem & { index: number; state: Exclude<Filter, "all"> };

function StatusPill({ state }: { state: Row["state"] }) {
  if (state === "completed") return <Pill className="bg-success-bg! text-success!">Completed</Pill>;
  if (state === "skipped") return <Pill tone="warn">Skipped</Pill>;
  return <Pill>Pending</Pill>;
}

/** The first input field, which is usually the one that says what the item is. */
const firstInput = (it: QueueItem) => preview(Object.values(it.input)[0], 90);

function columns(scores: ScoreConfig[]): Column<Row>[] {
  return [
    {
      header: "#",
      num: true,
      headClass: "w-12",
      cell: (r) => <span className="font-mono">{r.index + 1}</span>,
    },
    {
      header: "Input",
      cell: (r) => (
        <span className="block max-w-[320px] truncate" title={firstInput(r)}>
          {firstInput(r)}
        </span>
      ),
    },
    {
      header: "Agent output",
      cell: (r) => (
        <span className="block max-w-[240px] truncate font-mono text-xs text-muted">
          {preview(r.output, 90)}
        </span>
      ),
    },
    { header: "Status", cell: (r) => <StatusPill state={r.state} /> },
    ...scores.map((s): Column<Row> => ({
      header: s.name,
      cell: (r) => {
        const value = r.answers[s.id];
        if (!value) return <span className="text-faint">—</span>;
        const fail = s.kind === "pass_fail" && value === "fail";
        return <span className={fail ? "text-err" : ""}>{answerLabel(s, value)}</span>;
      },
    })),
    {
      header: "Correction",
      cell: (r) => {
        return r.corrected_output != null ? (
          <span className="text-xs">Corrected</span>
        ) : (
          <span className="text-faint">—</span>
        );
      },
    },
    {
      header: "Completed by",
      cell: (r) =>
        r.completed_by ? (
          <span className="flex flex-col">
            <span>{r.completed_by}</span>
            {r.completed_at && <span className="text-xs text-faint">{formatRelative(r.completed_at)}</span>}
          </span>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
  ];
}

export function QueueItems({
  items,
  scores,
  skipped,
  current,
  onOpen,
}: {
  items: QueueItem[];
  scores: ScoreConfig[];
  /** Skipped in this tab and still pending. */
  skipped: Set<string>;
  /** The index Review is on. */
  current: number;
  onOpen: (index: number) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const rows: Row[] = items.map((it, index) => ({
    ...it,
    index,
    state: it.status === "completed" ? "completed" : skipped.has(it.id) ? "skipped" : "pending",
  }));
  const count = (f: Filter) => (f === "all" ? rows.length : rows.filter((r) => r.state === f).length);
  const shown = filter === "all" ? rows : rows.filter((r) => r.state === filter);
  const filters: Filter[] = ["all", "pending", "skipped", "completed"];

  return (
    <Box>
      <div className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
        <Segmented
          label="Show items"
          value={filter}
          onChange={setFilter}
          items={filters.map((f) => ({ key: f, label: f[0]!.toUpperCase() + f.slice(1), count: count(f) }))}
        />
        <span className="text-xs text-muted">Click an item to review it.</span>
      </div>
      {shown.length ? (
        <DataTable
          columns={columns(scores)}
          rows={shown}
          onRowClick={(r) => onOpen(r.index)}
          rowProps={(r) => ({ "data-selected": r.index === current, "aria-current": r.index === current })}
        />
      ) : (
        <EmptyState title={`No ${filter} items`}>Pick another filter to see the rest.</EmptyState>
      )}
    </Box>
  );
}
